/* Diamond collection — client-side filter + sort.
   Facets are built from the items present on the page, so empty facets never show.
   Carat is a dual-handle range slider (diamond-shaped thumbs); the rest are chips.
   Works for both the card grid (.dcard) and the comparison list rows (.drow).
   No app dependency: works the moment the template renders.
*/
(function () {
  'use strict';

  var DIMENSIONS = ['type', 'color', 'clarity', 'cut']; // chip facets (carat = slider)

  // Display formatting per dimension.
  function label(dim, value) {
    if (dim === 'type') return value === 'lab-grown' ? 'Lab Grown' : 'Natural';
    if (dim === 'color' || dim === 'clarity') return value.toUpperCase();
    return value.charAt(0).toUpperCase() + value.slice(1); // cut
  }

  function norm(dim, raw) {
    if (raw == null) return '';
    return String(raw).trim().toLowerCase();
  }

  function sortValues(dim, values) {
    return Array.from(values).sort();
  }

  // "0.5 ct" / "2 ct" — no trailing .0
  function caratLabel(n) {
    var s = (Math.round(n * 10) / 10).toString();
    return s + ' ct';
  }

  function initCollection(root) {
    var grid = root.querySelector('[data-grid]');
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.dcard, .drow'));
    if (!cards.length) return;

    var countEl = root.querySelector('[data-count]');
    var noResults = root.querySelector('[data-noresults]');
    var activeWrap = root.querySelector('[data-active]');
    var priceMinEl = root.querySelector('[data-price-min]');
    var priceMaxEl = root.querySelector('[data-price-max]');
    var sortEl = root.querySelector('[data-sort]');
    var filtersAside = root.querySelector('[data-filters]');

    // ---- state ----
    var active = {};                 // dim -> Set(normalised values)
    DIMENSIONS.forEach(function (d) { active[d] = new Set(); });
    var priceMin = null, priceMax = null;
    var caratMin = null, caratMax = null;   // null = unbounded (full range)

    // ---- build facets from cards ----
    var facetValues = {};
    DIMENSIONS.forEach(function (d) { facetValues[d] = new Set(); });
    var minPrice = Infinity, maxPrice = -Infinity;
    var caratLo = Infinity, caratHi = -Infinity;

    cards.forEach(function (card) {
      DIMENSIONS.forEach(function (d) {
        var v = norm(d, card.dataset[d]);
        if (v) facetValues[d].add(v);
      });
      var p = parseInt(card.dataset.price, 10);
      if (!isNaN(p)) { if (p < minPrice) minPrice = p; if (p > maxPrice) maxPrice = p; }
      var c = parseFloat(card.dataset.carat);
      if (!isNaN(c)) { if (c < caratLo) caratLo = c; if (c > caratHi) caratHi = c; }
    });

    if (priceMinEl && minPrice !== Infinity) priceMinEl.placeholder = Math.floor(minPrice / 100);
    if (priceMaxEl && maxPrice !== -Infinity) priceMaxEl.placeholder = Math.ceil(maxPrice / 100);

    // Render chip groups; hide a facet that has 0–1 distinct value (nothing to filter).
    root.querySelectorAll('[data-facet]').forEach(function (group) {
      var dim = group.getAttribute('data-facet');
      if (DIMENSIONS.indexOf(dim) === -1) return; // carat + price handled separately
      var values = facetValues[dim];
      var chipsWrap = group.querySelector('[data-chips]');
      if (!values || values.size < 2 || !chipsWrap) { group.hidden = true; return; }
      sortValues(dim, values).forEach(function (val) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dcol__chip';
        btn.textContent = label(dim, val);
        btn.setAttribute('data-dim', dim);
        btn.setAttribute('data-val', val);
        btn.addEventListener('click', function () {
          if (active[dim].has(val)) { active[dim].delete(val); btn.classList.remove('is-active'); }
          else { active[dim].add(val); btn.classList.add('is-active'); }
          apply();
        });
        chipsWrap.appendChild(btn);
      });
    });

    // ---- carat range slider (dual handles, diamond thumbs) ----
    var crange = root.querySelector('[data-crange]');
    var crMin = root.querySelector('[data-crange-min]');
    var crMax = root.querySelector('[data-crange-max]');
    var crFill = root.querySelector('[data-crange-fill]');
    var crLo = root.querySelector('[data-crange-lo]');
    var crHi = root.querySelector('[data-crange-hi]');
    var crBounds = null; // [lo, hi] slider bounds

    if (crange && crMin && crMax) {
      var caratFacet = crange.closest('[data-facet]');
      if (caratLo === Infinity || caratHi - caratLo < 0.05) {
        if (caratFacet) caratFacet.hidden = true;
      } else {
        var lo = Math.floor(caratLo * 10) / 10;
        var hi = Math.ceil(caratHi * 10) / 10;
        crBounds = [lo, hi];
        [crMin, crMax].forEach(function (input) {
          input.min = lo; input.max = hi; input.step = 0.1;
        });
        crMin.value = lo; crMax.value = hi;

        var syncSlider = function (moved) {
          var a = parseFloat(crMin.value), b = parseFloat(crMax.value);
          if (a > b) { // handles crossed — push the other one along
            if (moved === crMin) { crMax.value = crMin.value; b = a; }
            else { crMin.value = crMax.value; a = b; }
          }
          var span = hi - lo || 1;
          if (crFill) {
            crFill.style.left = ((a - lo) / span * 100) + '%';
            crFill.style.right = (100 - (b - lo) / span * 100) + '%';
          }
          if (crLo) crLo.textContent = caratLabel(a);
          if (crHi) crHi.textContent = caratLabel(b);
          // keep the min thumb grabbable when both sit at the right edge
          crMin.style.zIndex = a > (lo + hi) / 2 ? 4 : 3;
          caratMin = a > lo ? a : null;
          caratMax = b < hi ? b : null;
        };
        var onSlide = function (e) { syncSlider(e.target); apply(); };
        crMin.addEventListener('input', onSlide);
        crMax.addEventListener('input', onSlide);
        syncSlider(crMin);
      }
    }

    function resetCaratSlider() {
      caratMin = caratMax = null;
      if (crBounds && crMin && crMax) {
        crMin.value = crBounds[0];
        crMax.value = crBounds[1];
        var evt;
        if (crFill) { crFill.style.left = '0%'; crFill.style.right = '0%'; }
        if (crLo) crLo.textContent = caratLabel(crBounds[0]);
        if (crHi) crHi.textContent = caratLabel(crBounds[1]);
      }
    }

    // ---- apply filters + sort ----
    function cardMatches(card) {
      for (var i = 0; i < DIMENSIONS.length; i++) {
        var d = DIMENSIONS[i];
        if (active[d].size && !active[d].has(norm(d, card.dataset[d]))) return false;
      }
      var c = parseFloat(card.dataset.carat);
      if (caratMin != null && !(c >= caratMin - 0.001)) return false;
      if (caratMax != null && !(c <= caratMax + 0.001)) return false;
      var p = parseInt(card.dataset.price, 10);
      if (priceMin != null && p < priceMin) return false;
      if (priceMax != null && p > priceMax) return false;
      return true;
    }

    function currentSort() { return sortEl ? sortEl.value : 'featured'; }

    function applySort(visibleCards) {
      var mode = currentSort();
      if (mode === 'featured') return visibleCards; // keep DOM order
      var sorted = visibleCards.slice();
      sorted.sort(function (a, b) {
        if (mode === 'price-asc')  return (+a.dataset.price) - (+b.dataset.price);
        if (mode === 'price-desc') return (+b.dataset.price) - (+a.dataset.price);
        if (mode === 'carat-asc')  return parseFloat(a.dataset.carat) - parseFloat(b.dataset.carat);
        if (mode === 'carat-desc') return parseFloat(b.dataset.carat) - parseFloat(a.dataset.carat);
        return 0;
      });
      return sorted;
    }

    function renderActiveChips() {
      if (!activeWrap) return;
      activeWrap.innerHTML = '';
      var any = false;
      DIMENSIONS.forEach(function (d) {
        active[d].forEach(function (val) {
          any = true;
          var chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'dcol__active-chip';
          chip.innerHTML = label(d, val) + ' <span aria-hidden="true">×</span>';
          chip.addEventListener('click', function () {
            active[d].delete(val);
            var src = root.querySelector('.dcol__chip[data-dim="' + d + '"][data-val="' + val + '"]');
            if (src) src.classList.remove('is-active');
            apply();
          });
          activeWrap.appendChild(chip);
        });
      });
      if (caratMin != null || caratMax != null) {
        any = true;
        var cc = document.createElement('button');
        cc.type = 'button';
        cc.className = 'dcol__active-chip';
        var cLo = caratMin != null ? caratMin : (crBounds ? crBounds[0] : 0);
        var cHi = caratMax != null ? caratMax : (crBounds ? crBounds[1] : 0);
        cc.innerHTML = caratLabel(cLo).replace(' ct', '') + '–' + caratLabel(cHi) + ' <span aria-hidden="true">×</span>';
        cc.addEventListener('click', function () { resetCaratSlider(); apply(); });
        activeWrap.appendChild(cc);
      }
      if (priceMin != null || priceMax != null) {
        any = true;
        var pc = document.createElement('button');
        pc.type = 'button';
        pc.className = 'dcol__active-chip';
        var lo = priceMin != null ? Math.floor(priceMin / 100) : '0';
        var hi = priceMax != null ? Math.ceil(priceMax / 100) : '∞';
        pc.innerHTML = 'Price ' + lo + '–' + hi + ' <span aria-hidden="true">×</span>';
        pc.addEventListener('click', function () {
          priceMin = priceMax = null;
          if (priceMinEl) priceMinEl.value = '';
          if (priceMaxEl) priceMaxEl.value = '';
          apply();
        });
        activeWrap.appendChild(pc);
      }
      activeWrap.hidden = !any;
    }

    function apply() {
      var visible = [];
      cards.forEach(function (card) {
        if (cardMatches(card)) { card.hidden = false; visible.push(card); }
        else { card.hidden = true; }
      });
      // Re-order DOM for sort (only the visible ones need correct order).
      applySort(visible).forEach(function (card) { grid.appendChild(card); });

      if (countEl) {
        // Any active client-side filter? (the page only holds one paginated
        // batch, so when NOTHING is filtered show the collection's REAL total,
        // not the ≤50 cards on this page — otherwise every collection reads "50".)
        var filtered = (caratMin != null || caratMax != null || priceMin != null || priceMax != null);
        DIMENSIONS.forEach(function (d) { if (active[d] && active[d].size) filtered = true; });
        var total = parseInt(countEl.getAttribute('data-total'), 10);
        if (isNaN(total)) total = cards.length;
        var n = filtered ? visible.length : total;
        countEl.textContent = n + (n === 1 ? ' diamond' : ' diamonds');
      }
      if (noResults) noResults.hidden = visible.length !== 0;
      renderActiveChips();
    }

    // ---- price inputs ----
    function onPrice() {
      var lo = priceMinEl && priceMinEl.value !== '' ? Math.round(parseFloat(priceMinEl.value) * 100) : null;
      var hi = priceMaxEl && priceMaxEl.value !== '' ? Math.round(parseFloat(priceMaxEl.value) * 100) : null;
      priceMin = (lo != null && !isNaN(lo)) ? lo : null;
      priceMax = (hi != null && !isNaN(hi)) ? hi : null;
      apply();
    }
    if (priceMinEl) priceMinEl.addEventListener('input', onPrice);
    if (priceMaxEl) priceMaxEl.addEventListener('input', onPrice);

    // ---- sort ----
    if (sortEl) sortEl.addEventListener('change', apply);

    // ---- clear all ----
    function clearAll() {
      DIMENSIONS.forEach(function (d) { active[d].clear(); });
      priceMin = priceMax = null;
      if (priceMinEl) priceMinEl.value = '';
      if (priceMaxEl) priceMaxEl.value = '';
      resetCaratSlider();
      root.querySelectorAll('.dcol__chip.is-active').forEach(function (c) { c.classList.remove('is-active'); });
      apply();
    }
    root.querySelectorAll('[data-clear]').forEach(function (b) { b.addEventListener('click', clearAll); });

    // ---- mobile filter toggle ----
    var toggle = root.querySelector('[data-filter-toggle]');
    if (toggle && filtersAside) {
      // On wide screens the aside is shown via CSS; the [hidden] attr is the mobile default.
      toggle.addEventListener('click', function () {
        var open = filtersAside.hasAttribute('hidden');
        if (open) { filtersAside.removeAttribute('hidden'); toggle.setAttribute('aria-expanded', 'true'); }
        else { filtersAside.setAttribute('hidden', ''); toggle.setAttribute('aria-expanded', 'false'); }
      });
    }

    apply(); // initial paint (count + any default sort)
  }

  // ---- collection intro: clamp to a few lines + "Learn more" toggle ----
  // Mobile shows products from the first scroll; the button only appears when the
  // text actually overflows the CSS line-clamp (nothing to expand → no button).
  function initIntroClamp(root) {
    var intro = root.querySelector('[data-intro]');
    var more = root.querySelector('[data-intro-more]');
    if (!intro || !more) return;

    function overflows() { return intro.scrollHeight - intro.clientHeight > 2; }

    // Reveal the button only if the clamped text is taller than its box.
    function refresh() {
      if (intro.classList.contains('is-expanded')) { more.hidden = false; return; }
      more.hidden = !overflows();
    }

    more.addEventListener('click', function () {
      var expanded = intro.classList.toggle('is-expanded');
      more.textContent = expanded ? 'Show less' : 'Learn more';
    });

    refresh();
    // Re-check after fonts/layout settle and on resize (clamp height is responsive).
    window.addEventListener('load', refresh);
    var t;
    window.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(refresh, 150); });
  }

  function boot() {
    document.querySelectorAll('.dcol').forEach(function (root) {
      initIntroClamp(root);
      initCollection(root);
    });
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
